import { match, P } from 'ts-pattern';
import get from 'lodash.get';

import { decompressGzippedData } from '~/utils/compression';

import type {
  Pagination,
  PusherEnvelope,
  PusherMessage,
  PusherReadResult,
} from '../types/pusher.types';

const UNSUPPORTED_COMPRESSION = 'Unsupported compression';
const INVALID_DATA = 'Invalid data';
const UNKNOWN_ERROR = 'Unknown error';

export const isPusherMessage = <T>(value: unknown): value is PusherMessage<T> =>
  get(value, 'pusherSettings.requestId', '').length > 0;

export const readRequestId = (payload: unknown): string | null =>
  isPusherMessage(payload) ? payload.pusherSettings.requestId : null;

export const isForRequest = <T>(message: PusherMessage<T>, requestId: string | null): boolean =>
  requestId !== null && message.pusherSettings.requestId === requestId;

export const parseEnvelope = <T>(message: PusherMessage<T>): PusherEnvelope<T> | null =>
  match(message)
    .returnType<PusherEnvelope<T> | null>()
    .with({ compression: 'gzip', response: P.string }, ({ response }) =>
      decompressGzippedData<PusherEnvelope<T>>(response)
    )
    .with({ compression: P.string }, () => null)
    .with({ response: P.string }, ({ response }) => JSON.parse(response) as PusherEnvelope<T>)
    .with({ response: P.not(P.string) }, ({ response }) => response)
    .exhaustive();

export const toPagination = <T>(envelope: PusherEnvelope<T>): Pagination => ({
  page: envelope.offset,
  limit: envelope.limit,
  total: envelope.total,
});

export const readPusherMessage = <T>(
  payload: unknown,
  activeRequestId: string | null
): PusherReadResult<T> => {
  if (!isPusherMessage<T>(payload) || !isForRequest(payload, activeRequestId)) {
    return { status: 'ignored' };
  }

  try {
    const envelope = parseEnvelope<T>(payload);

    if (!envelope) {
      return { status: 'error', message: UNSUPPORTED_COMPRESSION };
    }

    if (!envelope.data || envelope.error) {
      return { status: 'error', message: envelope.error || INVALID_DATA };
    }

    return { status: 'ok', data: envelope.data, pagination: toPagination(envelope) };
  } catch (error) {
    return { status: 'error', message: error instanceof Error ? error.message : UNKNOWN_ERROR };
  }
};
